import * as React from 'react'
import type { ComponentType, JSX } from 'react'

import {
  BlueskyIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  MastodonIcon,
  PinterestIcon,
  ThreadsIcon,
  TiktokIcon,
  TwitterIcon,
  YoutubeIcon,
} from '@buffer-mono/popcorn'
import type { IconProps } from '@buffer-mono/popcorn'

import {
  BarChart,
  BreakdownBarChart,
  ChartColorDot,
  ChartSeriesProvider,
  NetChangeBarChart,
  resolveLightColor,
  SnapshotChartThemeProvider,
  TimeSeriesChart,
} from '../charts'
import type { ChartSeriesInput } from '../charts'
import { StackedBarChart } from '../MetricChart/StackedBarChart'
import type { BarChartSeries } from '../MetricChart/StackedBarChart'
import type { MetricSeries } from '../MetricChart/types'
import {
  buildMetricPaletteIndex,
  metricColorToken,
  toChartSeriesInputs,
} from '../MetricsNarrativeChart/chartSeriesInputs'
import { ContentImpactChart } from '../MetricsNarrativeChart/ContentImpactChart'

import styles from './ChartSnapshot.module.css'

/**
 * Adapt time-series metrics to the generic bar primitives' simple series shape,
 * dropping any positionally-aligned comparison overlay — the bar views don't
 * render one.
 */
function toBarSeries(metrics: MetricSeries[]): BarChartSeries[] {
  const paletteIndexByKey = buildMetricPaletteIndex(metrics.map((m) => m.key))
  return metrics
    .filter((metric) => !metric.key.endsWith('-comparison'))
    .map((metric) => ({
      key: metric.key,
      label: metric.title,
      color: metricColorToken(metric.key, paletteIndexByKey),
      icon: metric.icon,
      data: metric.data.map((point) => ({
        date: point.date,
        value: point.value ?? null,
      })),
    }))
}

// Renders the chart at a fixed-pixel height that's larger than the live UI
// to give the social-friendly card the more-square feel. Recharts'
// `ResponsiveContainer` (inside `<TimeSeriesChart>`) takes care of the width.
const SNAPSHOT_CHART_HEIGHT = 520
// The live charts fit as many 48px-spaced gridlines as their height allows (~5);
// at the snapshot's taller height that would be ~11. Pin the count so the shared
// image matches the on-screen chart (the gridlines just space out to fill the card).
const SNAPSHOT_Y_TICK_COUNT = 5

/**
 * Followers breakdown view to snapshot instead of the time-series chart.
 * The All-Channels followers chart can be viewed as ranked per-channel bars
 * (`byChannel`) or diverging net-change bars (`growth`); when set, the snapshot
 * renders the matching breakdown chart so the shared image matches the on-screen
 * view. @see BreakdownBarChart @see NetChangeBarChart
 */
export interface FollowersBreakdownSnapshot {
  variant: 'byChannel' | 'growth'
  series: ChartSeriesInput[]
}

/**
 * Ranked horizontal bars to snapshot instead of the time-series chart — e.g.
 * the Posts "Bar" view (total posts per channel). When set, the snapshot
 * renders a `<BarChart>` so the shared image matches the on-screen view.
 * @see BarChart
 */
export interface RankedBarsSnapshot {
  series: ChartSeriesInput[]
  /** Caption centred under the value axis, e.g. "Posts sent". */
  xAxisLabel?: string
}

export interface ChartSnapshotProps {
  channelName?: string
  /** Raw service identifier (e.g. "instagram"); drives the avatar's icon overlay. */
  channelService?: string
  /** Avatar URL — caller is responsible for CORS-safe proxying. */
  channelAvatarUrl?: string
  /** Headline (e.g. "909 Followers"). */
  heading: string
  /** Sub-headline (e.g. "May 3 - Jun 2, 2026"). */
  subheading?: string
  /**
   * Followers breakdown view to render instead of the time-series chart. Takes
   * precedence over `chartMetrics` when set. @see FollowersBreakdownSnapshot
   */
  followersBreakdown?: FollowersBreakdownSnapshot
  /**
   * Ranked horizontal bars to render instead of the time-series chart. Takes
   * precedence over `chartMetrics` when set. @see RankedBarsSnapshot
   */
  rankedBars?: RankedBarsSnapshot
  /** Chart data — rendered as stacked bars (`chartStacked`) or a line. */
  chartMetrics?: MetricSeries[]
  chartStartDate?: Date
  chartEndDate?: Date
  /** Render the time-series data as stacked bars rather than a line. */
  chartStacked?: boolean
  /** Render as a composed bar (posts) + line (followers) chart — the Content
   * Impact view. Takes precedence over the line/stacked rendering. @see ContentImpactChart */
  chartComposed?: boolean
  /** Force a single shared Y-axis (All-Channels). @see TimeSeriesChart */
  chartSingleAxis?: boolean
  /** Month-only date labels (no day) for monthly-bucketed data. @see TimeSeriesChart */
  chartMonthly?: boolean
  /** Y-axis label (metric name). Needed when series titles are channel names
   * rather than the metric, e.g. All-Channels. @see TimeSeriesChart */
  chartYAxisLabel?: string
}

type IconComp = ComponentType<IconProps>

const CHANNEL_ICONS: Record<string, IconComp> = {
  bluesky: BlueskyIcon,
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- GraphQL service id is snake_case
  instagram_login_api: InstagramIcon,
  linkedin: LinkedinIcon,
  mastodon: MastodonIcon,
  pinterest: PinterestIcon,
  threads: ThreadsIcon,
  tiktok: TiktokIcon,
  twitter: TwitterIcon,
  x: TwitterIcon,
  youtube: YoutubeIcon,
}

const CHANNEL_BRAND_COLORS: Record<string, string> = {
  bluesky: '#1285FE',
  facebook: '#1877F2',
  googlebusiness: '#95BF47',
  instagram: '#F00276',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- GraphQL service id is snake_case
  instagram_login_api: '#F00276',
  linkedin: '#2867B2',
  mastodon: '#6364FF',
  pinterest: '#E60023',
  startPage: '#1F8B4F',
  threads: '#000000',
  tiktok: '#000000',
  twitter: '#000000',
  x: '#000000',
  youtube: '#FF0000',
}

const DEFAULT_BRAND_COLOR = '#1F8B4F'

/**
 * Small horizontal legend rendered under the chart in the snapshot — a colour
 * swatch + title per non-comparison series. The swatch is the same
 * {@link ChartColorDot} the live chart legend uses, coloured to match the
 * series' line/bar so the legend reads correctly (CORE-6264). Colours are
 * resolved to their light-theme values because the snapshot renders on a fixed
 * light card regardless of the app's current theme. @see snapshotChartTheme
 */
function ChartLegend({ metrics }: { metrics: MetricSeries[] }): JSX.Element {
  // Filter out the positionally-aligned comparison sibling lines — they're
  // labelled as "X (comparison)" via the live chart's existing scheme but
  // would duplicate the primary entry in a legend that's about *what* the
  // line measures.
  const items = metrics.filter((m) => !m.key.endsWith('-comparison'))
  const paletteIndexByKey = buildMetricPaletteIndex(metrics.map((m) => m.key))
  const labelColor = resolveLightColor('var(--color-text-subtle)')
  return (
    <ul className={styles.legend} aria-label="Chart legend">
      {items.map((metric) => (
        <li key={metric.key} className={styles.legendItem}>
          <ChartColorDot
            color={resolveLightColor(
              metricColorToken(metric.key, paletteIndexByKey),
            )}
            dashed={metric.dashed}
          />
          <span className={styles.legendLabel} style={{ color: labelColor }}>
            {metric.title}
          </span>
        </li>
      ))}
    </ul>
  )
}

function BufferLogo(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="39"
      viewBox="0 0 74 19"
      width="160"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        clipRule="evenodd"
        d="M0 4.23717L8.03416 0L16.1549 4.23717L8.03416 8.48914L0 4.23717ZM48.2324 6.29724V6.19638C48.2324 4.79863 49.0398 4.35193 50.3998 4.4528V1.90229C47.1416 1.70055 45.5975 3.35766 45.5975 6.19638V6.29724H44.1242V8.84775H45.5975V16.5713H48.2324V8.84775H49.7904H50.3998H51.2637V16.5713H53.8986V8.84775H56.066V6.29724H53.8986V6.19638C53.8986 4.79863 54.6918 4.35193 56.066 4.4528V1.90229C52.8079 1.70055 51.2637 3.35766 51.2637 6.19638V6.29724H50.3998H49.7904H48.2324ZM29.8166 9.09245C30.6524 8.41523 31.1623 7.44975 31.1623 6.21054C31.1623 3.80411 29.2358 2.1614 26.8134 2.1614H21.1896V16.5423H27.2667C29.7457 16.5423 31.7289 14.8419 31.7289 12.3779C31.7006 10.8937 30.964 9.75529 29.8166 9.09245ZM26.8276 4.8272C27.7342 4.8272 28.3575 5.50446 28.3575 6.41227C28.3575 7.32007 27.7058 7.99735 26.8276 7.99735H24.0085V4.8272H26.8276ZM27.2526 13.9053H24.0085V10.519H27.2526C28.2159 10.519 28.9099 11.2395 28.9099 12.2049C28.9099 13.1848 28.2159 13.9053 27.2526 13.9053ZM39.9451 6.28238V11.8301C39.9451 13.617 38.9819 14.3807 37.6928 14.3807C36.5029 14.3807 35.667 13.6601 35.667 12.2624V6.28238H33.0322V12.5939C33.0322 15.3317 34.7179 16.8447 36.8995 16.8447C38.2736 16.8447 39.336 16.3259 39.931 15.4037V16.5565H42.5658V6.28238H39.9451ZM66.9877 12.5076H59.2818C59.6643 13.8622 60.7691 14.4385 62.1433 14.4385C63.1773 14.4385 63.9991 14.0063 64.4379 13.4155L66.563 14.6403C65.614 16.038 64.084 16.845 62.1147 16.845C58.6867 16.845 56.5194 14.4818 56.5194 11.4125C56.5194 8.34328 58.7008 5.98006 61.9167 5.98006C64.934 5.98006 67.0873 8.38647 67.0873 11.4125C67.0873 11.816 67.0445 12.1618 66.9877 12.5076ZM61.9167 8.38647C60.4997 8.38647 59.5367 9.15023 59.2391 10.4615H64.4519C64.1262 8.97726 63.0214 8.38647 61.9167 8.38647ZM70.8977 8.05523V6.28286H68.2629V16.557H70.8977V11.6432C70.8977 9.48178 72.6258 8.86218 74 9.03509V6.08108C72.7113 6.08108 71.422 6.6575 70.8977 8.05523ZM8.03416 15.7599L2.55304 12.7337L0 14.1388L8.03416 18.57L16.1549 14.1388L13.573 12.7337L8.03416 15.7599ZM2.55304 7.95856L8.03416 10.7029L13.573 7.95856L16.1549 9.23926L8.03416 13.2643L0 9.23926L2.55304 7.95856Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  )
}

/**
 * 1080 × 1080 React component that wraps a chart in the Insights share
 * snapshot chrome. The chart is re-rendered fresh by Recharts from the data
 * passed in props, sized for the snapshot card rather than the live UI.
 *
 * Mounted offscreen by `captureChartAsBlob` and captured to PNG via
 * `html-to-image`. Lives in `components/` so a Storybook story can mount it
 * for visual review.
 */
export function ChartSnapshot({
  channelName,
  channelService,
  channelAvatarUrl,
  heading,
  subheading,
  followersBreakdown,
  rankedBars,
  chartMetrics,
  chartStartDate,
  chartEndDate,
  chartStacked,
  chartComposed,
  chartSingleAxis,
  chartMonthly,
  chartYAxisLabel,
}: ChartSnapshotProps): JSX.Element {
  const [avatarFailed, setAvatarFailed] = React.useState(false)

  const ChannelIcon = channelService ? CHANNEL_ICONS[channelService] : undefined
  const brandColor = channelService
    ? (CHANNEL_BRAND_COLORS[channelService] ?? DEFAULT_BRAND_COLOR)
    : DEFAULT_BRAND_COLOR

  const hasAvatar = Boolean(channelAvatarUrl) || Boolean(channelName)

  return (
    // Force light-theme chart colours + subtle axis text: the snapshot is a
    // fixed light card, so it must not inherit the app's dark palette
    // (CORE-6264). @see snapshotChartTheme
    <SnapshotChartThemeProvider>
      <div className={styles.root}>
        <header>
          {hasAvatar ? (
            <div className={styles.avatarWrap}>
              {channelAvatarUrl && !avatarFailed ? (
                <img
                  alt=""
                  className={styles.avatar}
                  crossOrigin="anonymous"
                  src={channelAvatarUrl}
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <div className={styles.avatarFallback}>
                  {(channelName?.charAt(0) || '?').toUpperCase()}
                </div>
              )}
              {ChannelIcon ? (
                <div
                  className={styles.channelIcon}
                  style={{ backgroundColor: brandColor }}
                >
                  {/* The glyph inherits `currentColor` (white, from
                    `.channelIcon`) — a `color` prop would map to a theme text
                    token that turns grey in dark mode (CORE-6264). */}
                  <ChannelIcon size={20} />
                </div>
              ) : null}
            </div>
          ) : null}
          <h1 className={styles.title}>{heading}</h1>
          {subheading ? <p className={styles.dateRange}>{subheading}</p> : null}
        </header>

        <div className={styles.chartCardWrap}>
          <div className={styles.chartCard}>
            {followersBreakdown ? (
              // The breakdown charts carry their own axis labels, so the
              // <ChartLegend> below (which describes line series) isn't used here.
              <ChartSeriesProvider series={followersBreakdown.series}>
                {followersBreakdown.variant === 'growth' ? (
                  <NetChangeBarChart
                    ariaLabel="Net follower change by channel"
                    disableAnimations
                  />
                ) : (
                  <BreakdownBarChart
                    ariaLabel="Followers by channel"
                    disableAnimations
                  />
                )}
              </ChartSeriesProvider>
            ) : rankedBars ? (
              // Ranked bars label each row on the axis, so no <ChartLegend> here.
              <ChartSeriesProvider series={rankedBars.series}>
                <BarChart
                  ariaLabel="Posts by channel"
                  xAxisLabel={rankedBars.xAxisLabel}
                  disableAnimations
                />
              </ChartSeriesProvider>
            ) : chartMetrics && chartStartDate && chartEndDate ? (
              <>
                {chartComposed ? (
                  <ContentImpactChart
                    disableAnimations
                    metrics={chartMetrics}
                    startDate={chartStartDate}
                    endDate={chartEndDate}
                    height={SNAPSHOT_CHART_HEIGHT}
                    yTickCount={SNAPSHOT_Y_TICK_COUNT}
                  />
                ) : chartStacked ? (
                  <StackedBarChart
                    disableAnimations
                    endDate={chartEndDate}
                    height={SNAPSHOT_CHART_HEIGHT}
                    monthly={chartMonthly}
                    series={toBarSeries(chartMetrics)}
                    startDate={chartStartDate}
                    yAxisLabel={chartYAxisLabel}
                  />
                ) : (
                  <ChartSeriesProvider
                    series={toChartSeriesInputs(chartMetrics)}
                  >
                    <TimeSeriesChart
                      ariaLabel={chartYAxisLabel ?? 'Metrics over time'}
                      disableAnimations
                      endDate={chartEndDate}
                      height={SNAPSHOT_CHART_HEIGHT}
                      monthly={chartMonthly}
                      singleAxis={chartSingleAxis}
                      startDate={chartStartDate}
                      xAxisTickCount={4}
                      yAxisLabel={chartYAxisLabel}
                      yTickCount={SNAPSHOT_Y_TICK_COUNT}
                    />
                  </ChartSeriesProvider>
                )}
                <ChartLegend metrics={chartMetrics} />
              </>
            ) : null}
          </div>
        </div>

        <footer className={styles.footer}>
          <span className={styles.bufferLogo}>
            <BufferLogo />
          </span>
        </footer>
      </div>
    </SnapshotChartThemeProvider>
  )
}
